import { z } from "zod";

export const caseWorkflowSteps = [
  "creation",
  "analysis",
  "negotiation",
  "legal_analysis",
  "legal_process",
  "in_payment",
  "finalized",
] as const;

export const caseTerminalSteps = ["deleted", "archived"] as const;

/** Longest client-provided reference code a case may carry. */
export const EXTERNAL_CODE_MAX = 50;

export const CaseWorkflowStep = z.enum(caseWorkflowSteps);
export type CaseWorkflowStep = z.infer<typeof CaseWorkflowStep>;

export const CaseTerminalStep = z.enum(caseTerminalSteps);
export type CaseTerminalStep = z.infer<typeof CaseTerminalStep>;

export const CaseStep = z.enum([...caseWorkflowSteps, ...caseTerminalSteps]);
export type CaseStep = z.infer<typeof CaseStep>;

export const CaseType = z.enum([
  "third_party_claim",
  "road_conditions",
  "zero_km_warranty",
  "routine_maintenance",
  "hidden_defect",
  "debt_collection",
]);
export type CaseType = z.infer<typeof CaseType>;

export const PRESCRIPTION_PERIOD = {
  minYears: 1,
  maxYears: 10,
  defaultYears: 3,
} as const;

export const PrescriptionPeriodYearsSchema = z
  .number()
  .int()
  .min(PRESCRIPTION_PERIOD.minYears)
  .max(PRESCRIPTION_PERIOD.maxYears);

/**
 * The statute-of-limitations deadline, projected fresh from its inputs
 * instead of persisted — undefined until the case has an incident date.
 */
export const casePrescriptionDate = (kase: {
  incident?: { date?: string | null } | null;
  prescriptionPeriodYears?: number | null;
}): string | undefined => {
  const incidentDate = kase.incident?.date;
  if (!incidentDate) return undefined;

  return addYearsToIsoDate(
    incidentDate,
    kase.prescriptionPeriodYears ?? PRESCRIPTION_PERIOD.defaultYears,
  );
};

/** A debt entry's deadline — undefined until both its due date and the debt's period are known. */
export const debtEntryPrescriptionDate = (
  dueDate: string | null | undefined,
  prescriptionPeriodYears: number | null | undefined,
): string | undefined =>
  dueDate && prescriptionPeriodYears != null
    ? addYearsToIsoDate(dueDate, prescriptionPeriodYears)
    : undefined;

type DebtDates = {
  prescriptionPeriodYears?: number | null;
  entries?: readonly { dueDate?: string | null }[] | null;
} | null;

/** The most urgent entry's due date, which stands for the whole debt in lists and sorting. */
export const earliestDebtDueDate = (debt: DebtDates | undefined): string | undefined =>
  debt?.entries
    ?.flatMap(({ dueDate }) => (dueDate ? [dueDate] : []))
    .reduce<string | undefined>(
      (earliest, dueDate) => (earliest === undefined || dueDate < earliest ? dueDate : earliest),
      undefined,
    );

export const debtPrescriptionDate = (kase: { debt?: DebtDates }): string | undefined =>
  debtEntryPrescriptionDate(earliestDebtDueDate(kase.debt), kase.debt?.prescriptionPeriodYears);

export const projectedCasePrescriptionDate = (kase: {
  caseType?: CaseType | null;
  incident?: { date?: string | null } | null;
  debt?: DebtDates;
  prescriptionPeriodYears?: number | null;
}): string | undefined =>
  kase.caseType === "debt_collection" ? debtPrescriptionDate(kase) : casePrescriptionDate(kase);

/** UTC arithmetic keeps the runtime's local time zone from shifting the calendar date. */
const addYearsToIsoDate = (isoDate: string, years: number): string => {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number) as [
    number,
    number,
    number,
  ];

  return new Date(Date.UTC(year + years, month - 1, day)).toISOString().slice(0, 10);
};

export const isForwardCaseStep = (current: CaseWorkflowStep, target: CaseWorkflowStep): boolean =>
  caseWorkflowSteps.indexOf(target) > caseWorkflowSteps.indexOf(current);

/** Whether a forward move must first satisfy the documents and fields due by `target`. Payment, finalization and legal authorization never wait on them. */
export const requiresCaseStepDocumentsAndFields = (
  current: CaseStep,
  target: CaseWorkflowStep,
): boolean =>
  target !== "in_payment" &&
  target !== "finalized" &&
  (current !== "legal_analysis" || target !== "legal_process");

export const nextCaseWorkflowStep = (current: CaseWorkflowStep): CaseWorkflowStep => {
  const next = caseWorkflowSteps[caseWorkflowSteps.indexOf(current) + 1];
  return next ?? current;
};
